import React, {
  useImperativeHandle,
  forwardRef,
  useMemo,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import UnderlineExtension from '@tiptap/extension-underline';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { LinkPreview } from './LinkPreview';
import { fetchLinkPreview } from '../../../services/linkPreview';
import CustomMention from './CustomMention';
import Toolbar from './Toolbar';
import { TiptapGlobalStyles } from './styles';
import EmoticonsExtension from './Emoticons';
import Bold from '@tiptap/extension-bold';

//  Inline Loading Spinner Extension
export const linkPreviewLoadingKey = new PluginKey('linkPreviewLoading');

export const InlineLinkPreviewLoader = Extension.create({
  name: 'inlineLinkPreviewLoader',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: linkPreviewLoadingKey,

        state: {
          init: () => null,

          apply(tr, value) {
            const meta = tr.getMeta(linkPreviewLoadingKey);

            if (meta?.clear) {
              if (!meta.id || value?.id === meta.id) return null;
              return value;
            }

            if (meta?.url && typeof meta?.pos === 'number') {
              return {
                id: meta.id,
                url: meta.url,
                pos: meta.pos,
              };
            }

            if (value && tr.docChanged) {
              return {
                ...value,
                pos: tr.mapping.map(value.pos),
              };
            }

            return value;
          },
        },

        props: {
          decorations(state) {
            const loading = linkPreviewLoadingKey.getState(state);

            if (!loading) return null;

            const widget = Decoration.widget(
              loading.pos,
              () => {
                const wrapper = document.createElement('span');
                wrapper.className = 'inline-link-preview-loader';
                wrapper.setAttribute('contenteditable', 'false');
                wrapper.innerHTML =
                  '<span class="inline-link-preview-spinner"></span>';
                return wrapper;
              },
              { side: -1 }
            );

            return DecorationSet.create(state.doc, [widget]);
          },
        },
      }),
    ];
  },
});

const CustomBold = Bold.extend({
  parseHTML() {
    return [
      { tag: 'strong' },
      {
        tag: 'b',
        getAttrs: (node) => node.style.fontWeight !== 'normal' && null,
      },
      {
        style: 'font-weight',
        getAttrs: (value) => /^(bold(er)?|[6-9]\d{2,})$/.test(value) && null, // <--- [6-9] includes 600, 700, 800, 900
      },
    ];
  },
});

// Main Tiptap Editor Component
const TiptapEditor = forwardRef(
  (
    {
      placeholder = 'Type a message...',
      mentionUsers = [],
      disableMentions = false,
      showToolbar = false,
      onCloseToolbar,
      onSubmit,
      onKeyDown,
      children,
    },
    ref
  ) => {
    const containerRef = useRef(null);
    const isSingleLineRef = useRef(true);
    const [isFocused, setIsFocused] = useState(false);
    const [isSingleLine, setIsSingleLine] = useState(true);
    const [, setSelectionTick] = useState(0);

    const showToolbarRef = useRef(showToolbar);
    useEffect(() => {
      showToolbarRef.current = showToolbar;
    }, [showToolbar]);

    const updateSingleLine = useCallback((val) => {
      isSingleLineRef.current = val;
      setIsSingleLine(val);
    }, []);

    const mentionExtension = useMemo(
      () => CustomMention(mentionUsers),
      [mentionUsers]
    );

    const measurementCanvasRef = useRef(null);
    const measureTextWidth = useCallback((text, font) => {
      if (!measurementCanvasRef.current) {
        measurementCanvasRef.current = document.createElement('canvas');
      }
      const ctx = measurementCanvasRef.current.getContext('2d');
      if (!ctx) return 0;
      ctx.font = font || '14px Arial, Helvetica, sans-serif';
      return ctx.measureText(text).width;
    }, []);

    const checkLineState = useCallback(
      (currentEditor) => {
        if (!currentEditor || currentEditor.isDestroyed) return;

        if (currentEditor.isEmpty) {
          updateSingleLine(true);
          return;
        }

        const container = containerRef.current;
        if (!container) return;

        const text = currentEditor.getText();

        if (text.includes('\n')) {
          updateSingleLine(false);
          return;
        }

        const dom = currentEditor.view?.dom;
        if (
          dom &&
          (dom.querySelector('ul') ||
            dom.querySelector('ol') ||
            dom.querySelector('pre') ||
            dom.querySelector('.link-preview-node-wrapper') ||
            dom.querySelectorAll('p').length > 1)
        ) {
          updateSingleLine(false);
          return;
        }

        const buttonsRow = container.querySelector('.composer-bottom-row');
        const actionsWidth = buttonsRow ? buttonsRow.offsetWidth : 160;

        let computedFont = '14px Arial, sans-serif';
        if (dom) {
          const domStyle = window.getComputedStyle(dom);
          computedFont = `${domStyle.fontWeight || '400'} ${
            domStyle.fontSize || '14px'
          } ${domStyle.fontFamily || 'Ubuntu, sans-serif'}`;
        }

        const textPixelWidth = measureTextWidth(text, computedFont);
        const maxSingleLineWidth = Math.max(
          100,
          container.clientWidth - actionsWidth - 24
        );

        if (textPixelWidth >= maxSingleLineWidth) {
          updateSingleLine(false);
        } else {
          updateSingleLine(true);
        }
      },
      [updateSingleLine, measureTextWidth]
    );

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          link: false,
          bold: false,
        }),
        CustomBold,
        UnderlineExtension,
        EmoticonsExtension,
        Link.configure({
          openOnClick: true,
          autolink: true,
          linkOnPaste: false,
          inclusive: false,
          HTMLAttributes: {
            class: 'message-link',
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        }),
        Placeholder.configure({
          placeholder,
        }),
        mentionExtension,
        LinkPreview,
        InlineLinkPreviewLoader,
      ],

      editorProps: {
        attributes: {
          class: 'tiptap-editor-input',
          spellcheck: 'true',
        },

        handleKeyDown: (view, event) => {
          if (event.key === 'Enter') {
            if (event.ctrlKey || event.metaKey) {
              event.preventDefault();
              view.dispatch(
                view.state.tr.setMeta(linkPreviewLoadingKey, { clear: true })
              );
              onSubmit?.();
              return true;
            }

            if (event.shiftKey) {
              return false;
            }

            if (showToolbarRef.current) {
              return false;
            }

            const isInsideList =
              view.state.schema.nodes.bulletList &&
              (view.state.doc.resolve(view.state.selection.from).parent.type
                .name === 'listItem' ||
                view.state.doc.resolve(view.state.selection.from).parent.type
                  .name === 'bulletList' ||
                view.state.doc.resolve(view.state.selection.from).parent.type
                  .name === 'orderedList');

            if (isInsideList) {
              return false;
            }

            event.preventDefault();
            view.dispatch(
              view.state.tr.setMeta(linkPreviewLoadingKey, { clear: true })
            );
            onSubmit?.();
            return true;
          }

          if (onKeyDown) {
            onKeyDown(event);
          }
          return false;
        },

        // Prevents typed text immediately following a link from inheriting the link format
        handleTextInput: (view, from, to, text) => {
          const { state } = view;
          const linkMarkType = state.schema.marks.link;

          if (!linkMarkType || from !== to) return false;

          const $from = state.doc.resolve(from);
          const nodeBefore = $from.nodeBefore;
          const nodeAfter = $from.nodeAfter;

          const isAfterLink =
            !!nodeBefore && !!linkMarkType.isInSet(nodeBefore.marks);

          const isBeforeSameLink =
            !!nodeAfter && !!linkMarkType.isInSet(nodeAfter.marks);

          if (isAfterLink && !isBeforeSameLink) {
            view.dispatch(
              state.tr
                .removeStoredMark(linkMarkType)
                .insertText(text, from, to)
                .removeStoredMark(linkMarkType)
                .setStoredMarks([])
            );
            return true;
          }

          return false;
        },

        handlePaste: (view, event) => {
          const text = event.clipboardData?.getData('text/plain') || '';
          // Match all URLs in pasted text
          const urlRegex = /(https?:\/\/[^\s]+)/gi;
          const matches = [...text.matchAll(urlRegex)];

          if (matches.length > 0) {
            event.preventDefault();
            const firstUrl = matches[0][0];
            const loadingId = `${Date.now()}-${Math.random()}`;

            const { state } = view;
            const { from } = state.selection;
            const spinnerPos = from;

            // Check if ANY link preview card already exists in the editor
            let hasAnyPreview = false;
            view.state.doc.descendants((node) => {
              if (node.type.name === 'linkPreview') {
                hasAnyPreview = true;
              }
            });

            // 1. Insert pasted text
            const tr = state.tr.insertText(text, from);

            // 2. Mark ALL pasted URLs as hyperlinks (like MS Teams)
            matches.forEach((m) => {
              const url = m[0];
              const urlStart = m.index;
              const linkFrom = from + urlStart;
              const linkTo = linkFrom + url.length;

              const linkMark = state.schema.marks.link.create({
                href: url,
                target: '_blank',
                rel: 'noopener noreferrer',
                class: 'message-link',
              });

              tr.addMark(linkFrom, linkTo, linkMark);
            });

            // 3. Clear stored link format so next typing is plain text
            tr.removeStoredMark(state.schema.marks.link).setStoredMarks([]);

            // 4. Add clean space after text if it ends with a link
            if (text.trimEnd().endsWith(matches[matches.length - 1][0])) {
              tr.insertText(' ', from + text.length)
                .removeStoredMark(state.schema.marks.link)
                .setStoredMarks([]);
            }

            // 5. Only show loading spinner if no preview card is currently shown
            if (!hasAnyPreview) {
              tr.setMeta(linkPreviewLoadingKey, {
                id: loadingId,
                url: firstUrl,
                pos: spinnerPos,
              });
            }

            view.dispatch(tr);

            // If a preview card already exists, do not fetch or create another one (Teams behavior)
            if (hasAnyPreview) return true;

            // Fetch preview ONLY for the first URL
            fetchLinkPreview(firstUrl)
              .then((meta) => {
                view.dispatch(
                  view.state.tr.setMeta(linkPreviewLoadingKey, {
                    clear: true,
                    id: loadingId,
                  })
                );

                if (!meta?.hasPreview) return;

                const currentState = view.state;
                if (!currentState.doc.textContent.includes(firstUrl)) return;

                // Double check no other preview was added during async fetch
                let alreadyHasPreview = false;
                currentState.doc.descendants((node) => {
                  if (node.type.name === 'linkPreview') {
                    alreadyHasPreview = true;
                  }
                });

                if (alreadyHasPreview) return;

                // Insert the single preview card at the end of the editor content
                view.dispatch(
                  currentState.tr.insert(
                    currentState.doc.content.size,
                    currentState.schema.nodes.linkPreview.create({
                      url: meta.url,
                      title: meta.title,
                      description: meta.description,
                      image: meta.image,
                    })
                  )
                );
              })
              .catch(() => {
                view.dispatch(
                  view.state.tr.setMeta(linkPreviewLoadingKey, {
                    clear: true,
                    id: loadingId,
                  })
                );
              });

            return true;
          }
          return false;
        },
      },

      onFocus: () => setIsFocused(true),
      onBlur: () => setIsFocused(false),

      // Automatically removes preview card if the URL text was deleted or altered
      onUpdate: ({ editor: currentEditor }) => {
        const text = currentEditor.getText({ blockSeparator: '\n\n' });

        if (!currentEditor.getText().trim()) {
          currentEditor.view.dispatch(
            currentEditor.view.state.tr.setMeta(linkPreviewLoadingKey, {
              clear: true,
            })
          );
        }

        const { state } = currentEditor;
        const tr = state.tr;
        let hasChange = false;

        state.doc.descendants((node, pos) => {
          if (node.type.name === 'linkPreview') {
            const url = node.attrs.url;
            if (!text.includes(url)) {
              tr.delete(pos, pos + node.nodeSize);
              hasChange = true;
            }
          }
        });

        if (hasChange) {
          currentEditor.view.dispatch(tr);
          return;
        }

        checkLineState(currentEditor);
      },

      onSelectionUpdate: ({ editor: currentEditor }) => {
        checkLineState(currentEditor);
        setSelectionTick((t) => t + 1);
      },
      onTransaction: ({ editor: currentEditor }) => {
        checkLineState(currentEditor);
        setSelectionTick((t) => t + 1);
      },
    });

    useEffect(() => {
      if (editor?.storage?.mention) {
        editor.storage.mention.mentionUsers = mentionUsers || [];
        editor.storage.mention.disableMentions = !!disableMentions;
      }
    }, [editor, mentionUsers, disableMentions]);

    useImperativeHandle(ref, () => ({
      getHTML: () => {
        if (!editor) return '';
        return editor.isEmpty ? '' : editor.getHTML();
      },
      getText: () => {
        if (!editor) return '';
        return editor.getText();
      },
      isEmpty: () => {
        return !editor || editor.isEmpty;
      },
      clearContent: () => {
        editor?.commands.clearContent();
        editor?.view.dispatch(
          editor.view.state.tr.setMeta(linkPreviewLoadingKey, { clear: true })
        );
        updateSingleLine(true);
      },
      setContent: (content) => {
        editor?.commands.setContent(content || '');
        if (editor) checkLineState(editor);
      },
      insertEmoji: (emoji) => {
        editor?.chain().focus().insertContent(emoji).run();
        if (editor) checkLineState(editor);
      },
      focus: () => {
        editor?.commands.focus();
      },
    }));

    const isSingle = isSingleLine && !showToolbar;

    return (
      <>
        <TiptapGlobalStyles />
        <div
          ref={containerRef}
          className={`composer-outer-box ${isFocused ? 'is-focused' : ''}`}
          onClick={(e) => {
            if (
              e.target.closest('button') ||
              e.target.closest('input') ||
              e.target.closest('.tiptap-toolbar') ||
              e.target.closest('.link-preview-node-wrapper')
            ) {
              return;
            }
            editor?.commands.focus();
          }}
        >
          {showToolbar && <Toolbar editor={editor} onClose={onCloseToolbar} />}

          <div
            className={`composer-inner-body ${
              isSingle ? 'is-single-row' : 'is-multi-col'
            }`}
          >
            <div className="editor-area-wrapper">
              <EditorContent editor={editor} />
            </div>
            <div className="composer-actions-wrapper">{children}</div>
          </div>
        </div>
      </>
    );
  }
);

TiptapEditor.displayName = 'TiptapEditor';

export default React.memo(TiptapEditor);
